import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dropdown, message, Tooltip } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import DropdownMenuButton from '@components/EntitiesDropdownMenu/DropdownMenuButton';
import { ApprovalWorkflowItemDto } from '@api/Api';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';
import './EntitiesDropdownMenu.less';
import {
  QUERY_APPROVAL_LIST_APPROVAL_WORKFLOWS,
  useDeleteApprovalWorkflow,
  useRestartApprovalWorkflow,
  useUpdateApprovalWorkflow
} from '@hooks/queries/approval';
import { SuccessIcon } from '@icons';

const RestartApprovalModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/RestartApprovalModal')
);
const DeleteApprovalModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/DeleteApprovalModal')
);

type ApprovalDropDownMenuProps = {
  approval: Omit<ApprovalWorkflowItemDto, 'assets'>;
  refetchApprovalsData: () => void;
};

function ApprovalDropDownMenu({
  approval,
  refetchApprovalsData
}: ApprovalDropDownMenuProps) {
  const dispatch = useDispatch();
  const { user } = useAuth(true);
  const queryKey = [QUERY_APPROVAL_LIST_APPROVAL_WORKFLOWS];
  const {
    mutateAsync: updateApprovalWorkflow,
    isPaused: isLoadingUpdateApprovalWorkflow
  } = useUpdateApprovalWorkflow({
    queryKey
  });

  const {
    mutateAsync: restartApprovalWorkflow,
    isPending: isLoadingRestartApprovalWorkflow
  } = useRestartApprovalWorkflow({
    queryKey
  });

  const {
    mutateAsync: deleteApprovalWorkflow,
    isPending: isLoadingDeleteApprovalWorkflow
  } = useDeleteApprovalWorkflow({
    queryKey
  });

  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
  const [isOpenRestartConfirmation, setIsOpenRestartConfirmation] =
    useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  const isLoading =
    isLoadingUpdateApprovalWorkflow ||
    isLoadingRestartApprovalWorkflow ||
    isLoadingDeleteApprovalWorkflow;
  const isPaused = !!approval.isPaused;
  const canManage = !!approval.permissions.manage;

  const sendMixpanelEvent = () => {
    MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
      addToFavorite: false,
      deleteAsset: false,
      newAsset: false,
      externalLinkShare: false,
      approvalRouting: true,
      newCommentAsset: false,
      annotationLeft: false,
      taskFromComment: false
    });
  };

  const onRestart = async () => {
    if (isLoading) return;
    if (!approval?.id) return;
    try {
      await restartApprovalWorkflow({
        id: approval.id
      });
      sendMixpanelEvent();
      setIsOpenRestartConfirmation(false);
      message.success({
        key: 'aw-restarted',
        content: `${approval.name} approval routing has been restarted from the beginning.`,
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />,
        duration: 2
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const onDeleteAW = async () => {
    if (isLoading) return;
    if (!approval) return;
    try {
      await deleteApprovalWorkflow({
        id: approval.id
      });
      setDeleteConfirmation(false);
      sendMixpanelEvent();
      message.success({
        key: 'aw-deleted',
        content: `${approval.name} approval routing has been deleted.`,
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />,
        duration: 2
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const items: ItemType[] = [
    ...(canManage
      ? [
          {
            key: 'restart_approval',
            label: 'Restart',
            onClick: () => setIsOpenRestartConfirmation(true)
          },
          {
            key: 'pauseResume_approval',
            label: (
              <Tooltip
                overlayClassName="toggle_favorite_overlay"
                title={
                  !isPaused
                    ? 'Pause approval routing for all media in the batch approval'
                    : null
                }
                placement="right"
              >
                <div style={{ display: 'block' }}>
                  {isPaused ? 'Resume' : 'Pause'}
                </div>
              </Tooltip>
            ),
            onClick: async () => {
              await updateApprovalWorkflow({
                id: approval.id,
                isPaused: !isPaused
              });
              sendMixpanelEvent();
              refetchApprovalsData();
            }
          },
          {
            key: 'edit_approval',
            label: 'Edit approval',
            onClick: () => {
              dispatch(
                toggleApprovalModal({
                  visible: true,
                  permissions: {
                    create: true
                  },
                  campaignId: approval.campaignId,
                  approvalWorkflowId: approval.id,
                  callback: (aw) => {
                    if (aw) refetchApprovalsData();
                  }
                })
              );
            }
          }
        ]
      : []),
    {
      key: 'copy_link_approval',
      label: (
        <Tooltip
          overlayClassName="toggle_favorite_overlay"
          title="Only reviewers in this approval can access this link"
          placement="right"
        >
          Copy approval link
        </Tooltip>
      ),
      onClick: () => {
        navigator.clipboard.writeText(approval.url);
        message.success({
          content: 'Link copied',
          className: 'message-dark-modal',
          icon: <SuccessIcon className="anticon" />
        });
      }
    },
    ...(canManage
      ? [
          {
            key: 'delete_approval',
            label: 'Delete entire routing',
            onClick: () => setDeleteConfirmation(true)
          }
        ]
      : [])
  ];

  return (
    <>
      <Dropdown
        trigger={['click']}
        onOpenChange={(value) => setIsDropDownOpen(value)}
        open={isDropDownOpen}
        menu={{
          className: 'entities-dropdown-menu-overlay',
          onClick: (e) => {
            e.domEvent.stopPropagation();
            setIsDropDownOpen(false);
          },
          items
        }}
        overlayClassName="entities-dropdown-menu-overlay"
      >
        <DropdownMenuButton
          onOpenChange={setIsDropDownOpen}
          loading={isLoading}
        />
      </Dropdown>
      <RestartApprovalModal
        approvalWorkflowName={approval.name || ''}
        visible={isOpenRestartConfirmation}
        onCancel={() => setIsOpenRestartConfirmation(false)}
        onConfirm={onRestart}
      />
      <DeleteApprovalModal
        approvalWorkflowName={approval.name || ''}
        visible={deleteConfirmation}
        onCancel={() => setDeleteConfirmation(false)}
        onConfirm={onDeleteAW}
      />
    </>
  );
}

export default ApprovalDropDownMenu;
